import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
    BarChart3, Clock, LayoutGrid, Layers, List, Loader2, Play, Plus, Trash2, ToggleRight,
} from 'lucide-react';

import { getAllDomains } from '@/services/api/domains';
import { deleteSetup, getSetups, runSetup } from '@/services/api/price-structure';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import type { Setup } from '@/types/price-structure';

import {
    AGGRESSIVENESS, ENGINES, GRANULARITY, TYPE_SOURCE, asArray, engineOf, formatUsd, linearParamsOf,
    linearSummary, windowLabel,
} from './constants';
import { ActivationModal } from './components/ActivationModal';

type ViewMode = 'cards' | 'table';

const VIEW_STORAGE_KEY = 'price-structure:view';

/** A preferência de visualização sobrevive à navegação e ao reload. */
function readStoredView(): ViewMode {
    try {
        return localStorage.getItem(VIEW_STORAGE_KEY) === 'table' ? 'table' : 'cards';
    } catch {
        return 'cards';
    }
}

export default function PriceStructure() {
    const navigate = useNavigate();
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [runningId, setRunningId] = useState<number | null>(null);
    // Domínio cuja liberação está aberta no modal. Guarda o nome junto porque o
    // modal precisa dele no título e no toast, e a lista pode não ter carregado.
    const [liberando, setLiberando] = useState<{ id: number; name: string } | null>(null);
    const [view, setView] = useState<ViewMode>(readStoredView);

    const changeView = (next: ViewMode) => {
        setView(next);
        try {
            localStorage.setItem(VIEW_STORAGE_KEY, next);
        } catch {
            // modo privado ou storage cheio: a preferência só não persiste
        }
    };

    const { data: setups, isLoading } = useQuery({
        queryKey: ['price-structure-setups'],
        queryFn: getSetups,
    });

    // Nomes de todas as contas: setups de cascata nao seguem o seletor de conta.
    const { data: domains } = useQuery({ queryKey: ['domains', 'todas'], queryFn: getAllDomains });

    const setupList = asArray<Setup>(setups);

    const domainName = useMemo(() => {
        const map = new Map<number, string>();
        asArray<any>(domains).forEach((d) => map.set(d.id, d.domain));
        return map;
    }, [domains]);

    const runMutation = useMutation({
        mutationFn: (setupId: number) => runSetup(setupId),
        onMutate: (setupId) => setRunningId(setupId),
        onSettled: () => setRunningId(null),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['price-structure-setups'] });
            toast({
                title: 'Execução concluída',
                description: `${data.segments_written} de ${data.segments_found} segmentos gravados (${data.segments_skipped} pulados).`,
            });
        },
        onError: (error: any) =>
            toast({
                variant: 'destructive',
                title: 'Erro ao executar',
                description: error.response?.data?.detail || 'Falha ao rodar o setup.',
            }),
    });

    const deleteMutation = useMutation({
        mutationFn: (setupId: number) => deleteSetup(setupId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['price-structure-setups'] });
            toast({ title: 'Setup removido' });
        },
        onError: (error: any) =>
            toast({
                variant: 'destructive',
                title: 'Erro ao remover',
                description: error.response?.data?.detail || 'Falha ao remover o setup.',
            }),
    });

    const askAndDelete = (setup: Setup) => {
        const name = domainName.get(setup.domain_id) ?? `#${setup.domain_id}`;
        if (confirm(`Remover o setup de ${name}?`)) deleteMutation.mutate(setup.id);
    };

    return (
        <div className="p-6 space-y-6">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-900">Estrutura de preço</h1>
                    <p className="mt-1 text-sm text-gray-500">
                        Gera os steps de cascade de cada segmento a partir do desempenho real no GAM.
                        Um setup por domínio.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {setupList.length > 0 && (
                        <div className="flex rounded-lg border border-gray-200 bg-white p-0.5">
                            <ViewToggle
                                active={view === 'cards'}
                                onClick={() => changeView('cards')}
                                label="Cards"
                                icon={<LayoutGrid className="h-3.5 w-3.5" />}
                            />
                            <ViewToggle
                                active={view === 'table'}
                                onClick={() => changeView('table')}
                                label="Tabela"
                                icon={<List className="h-3.5 w-3.5" />}
                            />
                        </div>
                    )}
                    <Button onClick={() => navigate('/price-structure/configure')}>
                        <Plus className="mr-2 h-4 w-4" />
                        Configurar domínios
                    </Button>
                </div>
            </div>

            {isLoading && (
                <div className="flex items-center justify-center py-12">
                    <div className="flex items-center gap-3">
                        <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                        <span className="text-gray-600">Carregando...</span>
                    </div>
                </div>
            )}

            {!isLoading && setupList.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl border-2 border-dashed border-gray-200">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                        <Layers className="h-8 w-8 text-blue-500" />
                    </div>
                    <h3 className="mt-4 text-lg font-semibold text-gray-900">Nenhum setup configurado</h3>
                    <p className="mt-1 text-sm text-gray-500 max-w-md text-center">
                        Configure um ou mais domínios para o motor começar a gerar granulações
                        automaticamente na cadência escolhida.
                    </p>
                    <Button className="mt-4" onClick={() => navigate('/price-structure/configure')}>
                        <Plus className="mr-2 h-4 w-4" />
                        Configurar domínios
                    </Button>
                </div>
            )}

            {setupList.length > 0 &&
                (view === 'cards' ? (
                    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                        {setupList.map((setup) => (
                            <SetupCard
                                key={setup.id}
                                setup={setup}
                                domain={domainName.get(setup.domain_id) ?? `#${setup.domain_id}`}
                                running={runningId === setup.id}
                                onRun={() => runMutation.mutate(setup.id)}
                                onResults={() =>
                                    navigate(`/price-structure/results/${setup.domain_id}`)
                                }
                                onActivate={() =>
                                    setLiberando({
                                        id: setup.domain_id,
                                        name: domainName.get(setup.domain_id) ?? `#${setup.domain_id}`,
                                    })
                                }
                                onDelete={() => askAndDelete(setup)}
                            />
                        ))}
                    </div>
                ) : (
                    <SetupTable
                        setups={setupList}
                        domainName={domainName}
                        runningId={runningId}
                        onRun={(id) => runMutation.mutate(id)}
                        onActivate={(setup) =>
                            setLiberando({
                                id: setup.domain_id,
                                name: domainName.get(setup.domain_id) ?? `#${setup.domain_id}`,
                            })
                        }
                        onResults={(domainId) =>
                            navigate(`/price-structure/results/${domainId}`)
                        }
                        onDelete={askAndDelete}
                    />
                ))}

            <ActivationModal
                open={liberando !== null}
                onOpenChange={(aberto) => !aberto && setLiberando(null)}
                domainId={liberando?.id ?? null}
                domainName={liberando?.name ?? ''}
            />
        </div>
    );
}

interface SetupCardProps {
    setup: Setup;
    domain: string;
    running: boolean;
    onRun: () => void;
    onResults: () => void;
    /** Abre o modal de liberação: onde a estrutura passa a valer de verdade. */
    onActivate: () => void;
    onDelete: () => void;
}

function SetupCard({ setup, domain, running, onRun, onResults, onActivate, onDelete }: SetupCardProps) {
    const aggressiveness = AGGRESSIVENESS.find((a) => a.value === setup.aggressiveness);
    const granularity = GRANULARITY.find((g) => g.value === setup.granularity);
    const window = windowLabel(setup);
    const source = TYPE_SOURCE.find((t) => t.value === setup.type_source);
    const linear = engineOf(setup) === 'v4' ? linearParamsOf(setup) : null;

    const formatDate = (value: string | null) =>
        value ? new Date(value).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

    return (
        <div
            className={`bg-white rounded-xl border-2 overflow-hidden transition-all hover:shadow-lg ${
                setup.status === 'active' ? 'border-emerald-200' : 'border-gray-200'
            }`}
        >
            {/* Clicar no corpo do card abre as regras do domínio. */}
            <div
                role="button"
                tabIndex={0}
                onClick={onResults}
                onKeyDown={(e) => e.key === 'Enter' && onResults()}
                title="Ver as regras do domínio"
                className="p-5 space-y-4 cursor-pointer"
            >
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate">{domain}</h3>
                        <p className="text-xs text-gray-500 truncate">{source?.label}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                        <EngineBadge setup={setup} />
                        <span
                            className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                setup.status === 'active'
                                    ? 'bg-emerald-100 text-emerald-700'
                                    : 'bg-gray-100 text-gray-600'
                            }`}
                        >
                            {setup.status === 'active' ? '● Ativo' : '○ Inativo'}
                        </span>
                    </div>
                </div>

                {linear ? (
                    <div className="grid grid-cols-2 gap-2 text-xs">
                        <Fact label="Abertura" value={`eCPM × ${linear.open_multiplier.toFixed(2).replace('.', ',')}`} />
                        <Fact label="Chão" value={`${Math.round(linear.floor_fraction * 100)}% da abertura`} />
                        <Fact
                            label="Degraus"
                            value={`${setup.steps_count}${linear.fallback ? ' + 0,05' : ''}`}
                        />
                        <Fact label="Mín. receita" value={formatUsd(linear.min_revenue)} />
                    </div>
                ) : (
                    <div className="grid grid-cols-2 gap-2 text-xs">
                        <Fact label="Agressividade" value={aggressiveness?.label ?? '—'} />
                        <Fact label="Granularidade" value={granularity?.label ?? '—'} />
                        <Fact label="Steps pedidos" value={String(setup.steps_count)} />
                        <Fact label="Mín. impressões" value={String(setup.min_impressions)} />
                    </div>
                )}

                <div className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600">
                    <div className="flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5" />
                        {window?.label}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-3 text-gray-500">
                        <span>última: {formatDate(setup.last_run_at)}</span>
                        <span>próxima: {formatDate(setup.next_run_at)}</span>
                    </div>
                </div>
            </div>

            <div className="flex border-t border-gray-100 divide-x divide-gray-100">
                <button
                    onClick={onRun}
                    disabled={running}
                    className="flex-1 px-3 py-2.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 flex items-center justify-center gap-1.5"
                >
                    {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                    Rodar agora
                </button>
                <button
                    onClick={onResults}
                    className="flex-1 px-3 py-2.5 text-xs font-medium text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-1.5"
                >
                    <BarChart3 className="h-3.5 w-3.5" />
                    Resultados
                </button>
                <button
                    onClick={onActivate}
                    className="flex-1 px-3 py-2.5 text-xs font-medium text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-1.5"
                >
                    <ToggleRight className="h-3.5 w-3.5" />
                    Liberação
                </button>
                <button
                    onClick={onDelete}
                    className="px-3 py-2.5 text-xs font-medium text-red-600 hover:bg-red-50 flex items-center justify-center"
                    title="Remover setup"
                >
                    <Trash2 className="h-3.5 w-3.5" />
                </button>
            </div>
        </div>
    );
}

/** Qual motor gera a cascata deste domínio. */
function EngineBadge({ setup }: { setup: Setup }) {
    const engine = engineOf(setup);
    const info = ENGINES.find((e) => e.value === engine);
    const title =
        engine === 'v4'
            ? `${info?.label} — ${linearSummary(linearParamsOf(setup), setup.steps_count)}`
            : info?.label;

    return (
        <span
            title={title}
            className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                engine === 'v4' ? 'bg-violet-100 text-violet-700' : 'bg-gray-100 text-gray-500'
            }`}
        >
            {engine}
        </span>
    );
}

function Fact({ label, value }: { label: string; value: string }) {
    return (
        <div className="rounded-lg border border-gray-100 px-2.5 py-1.5">
            <div className="text-[10px] uppercase tracking-wide text-gray-400">{label}</div>
            <div className="mt-0.5 font-medium text-gray-800 truncate" title={value}>
                {value}
            </div>
        </div>
    );
}

function ViewToggle({
    active, onClick, label, icon,
}: {
    active: boolean;
    onClick: () => void;
    label: string;
    icon: React.ReactNode;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            title={`Visualizar em ${label.toLowerCase()}`}
            className={`flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors ${
                active ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
        >
            {icon}
            {label}
        </button>
    );
}

interface SetupTableProps {
    setups: Setup[];
    domainName: Map<number, string>;
    runningId: number | null;
    onRun: (setupId: number) => void;
    onResults: (domainId: number) => void;
    onActivate: (setup: Setup) => void;
    onDelete: (setup: Setup) => void;
}

/** Mesma informação dos cards, densa — útil quando há muitos domínios. */
function SetupTable({
    setups, domainName, runningId, onRun, onResults, onActivate, onDelete,
}: SetupTableProps) {
    return (
        <div className="rounded-xl border border-gray-200 bg-white">
            <table className="w-full table-fixed text-sm">
                <colgroup>
                    <col className="w-[20%]" />
                    <col className="w-[13%]" />
                    <col className="w-[13%]" />
                    <col className="w-[7%]" />
                    <col className="w-[9%]" />
                    <col className="w-[9%]" />
                    <col className="w-[17%]" />
                    <col className="w-[12%]" />
                </colgroup>
                <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                        <th className="px-4 py-3 font-medium">Domínio</th>
                        <th className="px-2 py-3 font-medium">Agressividade</th>
                        <th className="px-2 py-3 font-medium">Granularidade</th>
                        <th className="px-2 py-3 font-medium">Steps</th>
                        <th className="px-2 py-3 font-medium" title="v3: impressões · v4: receita em US$">
                            Mínimo
                        </th>
                        <th className="px-2 py-3 font-medium">Janela</th>
                        <th className="px-2 py-3 font-medium">Execuções</th>
                        <th className="px-4 py-3 font-medium">Ações</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {setups.map((setup) => (
                        <SetupRow
                            key={setup.id}
                            setup={setup}
                            domain={domainName.get(setup.domain_id) ?? `#${setup.domain_id}`}
                            running={runningId === setup.id}
                            onRun={() => onRun(setup.id)}
                            onResults={() => onResults(setup.domain_id)}
                            onActivate={() => onActivate(setup)}
                            onDelete={() => onDelete(setup)}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function SetupRow({
    setup, domain, running, onRun, onResults, onActivate, onDelete,
}: {
    setup: Setup;
    domain: string;
    running: boolean;
    onRun: () => void;
    onResults: () => void;
    onActivate: () => void;
    onDelete: () => void;
}) {
    const aggressiveness = AGGRESSIVENESS.find((a) => a.value === setup.aggressiveness);
    const granularity = GRANULARITY.find((g) => g.value === setup.granularity);
    const window = windowLabel(setup);
    const source = TYPE_SOURCE.find((t) => t.value === setup.type_source);
    const linear = engineOf(setup) === 'v4' ? linearParamsOf(setup) : null;

    return (
        <tr className="hover:bg-gray-50/50">
            <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                    <span
                        className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                            setup.status === 'active' ? 'bg-emerald-500' : 'bg-gray-300'
                        }`}
                        title={setup.status === 'active' ? 'Ativo' : 'Inativo'}
                    />
                    <span className="truncate font-medium text-gray-900" title={domain}>
                        {domain}
                    </span>
                </div>
                <div className="mt-0.5 flex items-center gap-1.5 pl-3.5 text-xs text-gray-500">
                    <EngineBadge setup={setup} />
                    {source?.short}
                </div>
            </td>
            {linear ? (
                <>
                    <td className="px-2 py-3 text-gray-700" title="Abertura = eCPM × multiplicador">
                        eCPM × {linear.open_multiplier.toFixed(2).replace('.', ',')}
                    </td>
                    <td className="px-2 py-3 text-gray-700" title="Chão, em % da abertura">
                        chão {Math.round(linear.floor_fraction * 100)}%
                    </td>
                    <td className="px-2 py-3 text-gray-700" title="Degraus + tentativa final a 0,05">
                        {setup.steps_count}
                        {linear.fallback ? ' + 0,05' : ''}
                    </td>
                </>
            ) : (
                <>
                    <td className="px-2 py-3 text-gray-700" title={aggressiveness?.label}>
                        {aggressiveness?.short ?? '—'}
                    </td>
                    <td className="px-2 py-3 text-gray-700" title={granularity?.label}>
                        {granularity?.short ?? '—'}
                    </td>
                    <td className="px-2 py-3 text-gray-700">{setup.steps_count}</td>
                </>
            )}
            <td className="px-2 py-3 text-gray-700" title={linear ? 'Receita mínima na janela' : undefined}>
                {linear ? formatUsd(linear.min_revenue) : setup.min_impressions.toLocaleString('pt-BR')}
            </td>
            <td className="px-2 py-3 text-gray-700" title={window.label}>
                {window.short}
            </td>
            <td className="px-2 py-3 text-xs text-gray-500">
                <div>última: {formatRunDate(setup.last_run_at)}</div>
                <div>próxima: {formatRunDate(setup.next_run_at)}</div>
            </td>
            <td className="px-4 py-3">
                <div className="flex items-center gap-1">
                    <IconAction
                        onClick={onRun}
                        disabled={running}
                        title="Rodar agora"
                        icon={
                            running ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                                <Play className="h-3.5 w-3.5" />
                            )
                        }
                    />
                    <IconAction
                        onClick={onResults}
                        title="Ver resultados"
                        icon={<BarChart3 className="h-3.5 w-3.5" />}
                    />
                    <IconAction
                        onClick={onActivate}
                        title="Liberação da estrutura"
                        icon={<ToggleRight className="h-3.5 w-3.5" />}
                    />
                    <IconAction
                        onClick={onDelete}
                        title="Remover setup"
                        danger
                        icon={<Trash2 className="h-3.5 w-3.5" />}
                    />
                </div>
            </td>
        </tr>
    );
}

function IconAction({
    onClick, title, icon, disabled, danger,
}: {
    onClick: () => void;
    title: string;
    icon: React.ReactNode;
    disabled?: boolean;
    danger?: boolean;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            title={title}
            className={`rounded-md p-1.5 transition-colors disabled:opacity-50 ${
                danger
                    ? 'text-red-500 hover:bg-red-50'
                    : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
            }`}
        >
            {icon}
        </button>
    );
}

function formatRunDate(value: string | null) {
    return value
        ? new Date(value).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
        : '—';
}
